import type { CameraName, CommandVector } from '@digger/shared';
import type { LatencyReport, RttTracker } from './latency.js';

export interface HudElements {
  connection: HTMLElement;
  controlRtt: HTMLElement;
  videoLatency: HTMLElement;
  dominant: HTMLElement;
  vector: HTMLElement;
  estopBanner: HTMLElement;
  staleBanner: HTMLElement;
}

const fmt = (v: number | undefined, unit = 'ms'): string =>
  v === undefined ? '—' : `${Math.round(v)}${unit}`;

/**
 * On-screen HUD: connection state, measured control RTT, decomposed
 * glass-to-glass estimate, active camera, live command vector, and the
 * two safety banners (E-STOP latch, stale control link).
 */
export class Hud {
  constructor(private readonly el: HudElements) {}

  setConnection(state: string, controlOpen: boolean): void {
    this.el.connection.textContent = `link: ${state}${controlOpen ? ' · control open' : ''}`;
    this.el.connection.dataset.ok = String(state === 'connected' && controlOpen);
  }

  setControlRtt(rtt: RttTracker, deadmanMs: number): void {
    const age = rtt.ackAgeMs();
    this.el.controlRtt.textContent = `control rtt: ${fmt(rtt.rttMs)} · last ack: ${fmt(age)}`;
    const stale = age !== undefined && age > deadmanMs;
    this.el.staleBanner.hidden = !stale;
    if (stale) {
      this.el.staleBanner.textContent = `CONTROL STALE (${fmt(age)}) — DIGGER AUTO-STOPPED`;
    }
  }

  setVideoLatency(report: LatencyReport, dominant: CameraName): void {
    const v = report.perCamera[dominant];
    const g2g = report.glassToGlassEstimateMs?.(dominant);
    this.el.videoLatency.textContent =
      `video[${dominant}]: ${fmt(v?.fps, '')}fps · net rtt ${fmt(report.networkRttMs)} · ` +
      `jitter ${fmt(v?.jitterBufferMs)} · decode ${fmt(v?.decodeMs)} · g2g≈ ${fmt(g2g)}`;
  }

  setDominant(camera: CameraName): void {
    this.el.dominant.textContent = `camera: ${camera} (V/Tab to swap)`;
  }

  setVector(v: CommandVector, sent: boolean): void {
    const n = (x: number) => x.toFixed(1).padStart(4);
    this.el.vector.textContent =
      `L${n(v.drive.left)} R${n(v.drive.right)} slew${n(v.slew)} ` +
      `boom${n(v.arm.boom)} dip${n(v.arm.dipper)} bkt${n(v.arm.bucket)}${sent ? '' : ' (NOT SENT)'}`;
  }

  setEstop(latched: boolean): void {
    this.el.estopBanner.hidden = !latched;
  }

  setDisplaced(): void {
    this.el.connection.textContent = 'link: DISPLACED — another operator took control';
    this.el.connection.dataset.ok = 'false';
  }
}
